import React, { useEffect, useState, useRef } from 'react';
import { PortalMessagesService } from '../../services/portal/portalMessagesService';
import { useClientPortal } from '../../context/ClientPortalContext';
import ChatBubble from '../../components/client/messages/ChatBubble';
import ChatInput from '../../components/client/messages/ChatInput';
import { ArrowPathIcon, ChatBubbleLeftRightIcon, UserCircleIcon } from '@heroicons/react/24/outline';

const ClientMessagesPage = () => {
  const { clientId, client } = useClientPortal();
  const [messages, setMessages] = useState([]);
  const [sending, setSending] = useState(false);
  const [loading, setLoading] = useState(true);
  const messagesEndRef = useRef(null);

  const loadMessages = async () => {
    if (!clientId) return;
    setLoading(true);
    const { data } = await PortalMessagesService.listThreads(clientId);
    // Sort by date ascending for chat display
    const sorted = (data || []).sort((a, b) =>
      new Date(a.created_at) - new Date(b.created_at)
    );
    setMessages(sorted);
    setLoading(false);
  };

  useEffect(() => {
    loadMessages();
  }, [clientId]);

  // Scroll to bottom when new message is added
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const sendMessage = async (body) => {
    if (!body.trim()) return;
    setSending(true);

    // Optimistic update
    const tempMessage = {
      id: `temp-${Date.now()}`,
      body,
      created_at: new Date().toISOString(),
      channel: 'portal',
      isOwn: true
    };
    setMessages(prev => [...prev, tempMessage]);

    const { error } = await PortalMessagesService.sendMessage({ clientId, body });
    setSending(false);

    if (error) {
      // Remove optimistic message on error
      setMessages(prev => prev.filter(m => m.id !== tempMessage.id));
      alert('Failed to send message. Please try again.');
    } else {
      loadMessages();
    }
  };

  // Determine if message is from client (own) based on channel or sender
  const isOwnMessage = (msg) => {
    return msg.channel === 'portal' || msg.sender_type === 'client' || msg.isOwn;
  };

  return (
    <div className="h-[calc(100vh-200px)] flex flex-col">
      {/* Header */}
      <div className="bg-white border border-slate-200 rounded-2xl p-4 shadow-sm mb-4 flex-shrink-0">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="h-12 w-12 rounded-full bg-gradient-to-br from-indigo-500 to-purple-500 flex items-center justify-center text-white text-lg font-semibold">
              RM
            </div>
            <div>
              <h1 className="text-lg font-bold text-slate-900">Your Consultant</h1>
              <p className="text-sm text-slate-500">Ravana Migration Team</p>
            </div>
          </div>
          <button
            onClick={loadMessages}
            disabled={loading}
            className="flex items-center gap-2 px-3 py-2 text-sm text-indigo-600 hover:bg-indigo-50 rounded-xl transition-colors"
          >
            <ArrowPathIcon className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </button>
        </div>
      </div>

      {/* Messages Container */}
      <div className="flex-1 bg-slate-50 border border-slate-200 rounded-2xl overflow-hidden flex flex-col">
        {/* Messages Area */}
        <div className="flex-1 overflow-y-auto p-4">
          {loading ? (
            <div className="flex items-center justify-center h-full">
              <div className="text-center">
                <div className="inline-block animate-spin rounded-full h-8 w-8 border-4 border-indigo-500 border-t-transparent mb-3"></div>
                <p className="text-sm text-slate-500">Loading messages...</p>
              </div>
            </div>
          ) : messages.length === 0 ? (
            <div className="flex items-center justify-center h-full">
              <div className="text-center">
                <div className="h-16 w-16 mx-auto mb-4 rounded-full bg-slate-100 flex items-center justify-center">
                  <ChatBubbleLeftRightIcon className="h-8 w-8 text-slate-400" />
                </div>
                <h3 className="text-lg font-medium text-slate-700 mb-1">No messages yet</h3>
                <p className="text-sm text-slate-500 max-w-xs">
                  Start a conversation with your consultant below
                </p>
              </div>
            </div>
          ) : (
            <>
              {/* Welcome message */}
              <div className="text-center mb-6">
                <p className="text-xs text-slate-400 mb-2">
                  Welcome to your secure messaging portal
                </p>
                <div className="inline-flex items-center gap-2 px-3 py-1 bg-slate-100 rounded-full text-xs text-slate-500">
                  <span className="h-2 w-2 rounded-full bg-emerald-500"></span>
                  Messages are encrypted and secure
                </div>
              </div>

              {/* Message bubbles */}
              {messages.map((message, idx) => {
                const isOwn = isOwnMessage(message);
                const showDate = idx === 0 ||
                  new Date(message.created_at).toDateString() !==
                  new Date(messages[idx - 1]?.created_at).toDateString();

                return (
                  <React.Fragment key={message.id}>
                    {showDate && (
                      <div className="text-center my-4">
                        <span className="px-3 py-1 bg-slate-100 rounded-full text-xs text-slate-500">
                          {new Date(message.created_at).toLocaleDateString('en-US', {
                            weekday: 'long',
                            month: 'short',
                            day: 'numeric'
                          })}
                        </span>
                      </div>
                    )}
                    <ChatBubble
                      message={message}
                      isOwn={isOwn}
                    />
                  </React.Fragment>
                );
              })}
              <div ref={messagesEndRef} />
            </>
          )}
        </div>

        {/* Input Area */}
        <div className="p-4 bg-white border-t border-slate-100">
          <ChatInput
            onSend={sendMessage}
            sending={sending}
            placeholder="Ask a question or share updates..."
          />
        </div>
      </div>
    </div>
  );
};

export default ClientMessagesPage;
